"use client";
/**
 * POC shim: maps the react-router-dom API surface the landing components use
 * onto Next's router. Lets the existing Vite/CRA components compile unchanged.
 */
import * as React from "react";
import NextLink from "next/link";
import { usePathname, useRouter } from "next/navigation";

type LinkProps = Omit<React.ComponentProps<typeof NextLink>, "href"> & {
  to: string;
};

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  ({ to, children, prefetch, ...rest }, ref) => (
    // POC: only "/" exists, so default prefetch=false to avoid RSC 404s for
    // routes like /pricing and /demo (those noisy 404s dinged Best-Practices).
    <NextLink ref={ref} href={to} prefetch={prefetch ?? false} {...rest}>
      {children}
    </NextLink>
  )
);
Link.displayName = "Link";

export const NavLink = Link;

export function useNavigate() {
  const router = useRouter();
  return (to: string | number) => {
    if (typeof to === "number") {
      // history delta (e.g. navigate(-1))
      if (typeof window !== "undefined") window.history.go(to);
      return;
    }
    router.push(to);
  };
}

export function useLocation() {
  const pathname = usePathname() || "/";
  return {
    pathname,
    search: "",
    hash: "",
    state: null,
    key: "default",
  };
}

// Real react-router-dom derives params from the matched <Route path> pattern.
// This shim has no such registry — PlatformApp.tsx's resolveRoute() does its own
// pathname regex matching per screen — so params are threaded through via context
// instead: resolveRoute wraps a matched dynamic route in a RouteParamsProvider
// carrying the values it already captured.
const RouteParamsContext = React.createContext<Record<string, string>>({});

export function RouteParamsProvider({
  params,
  children,
}: {
  params: Record<string, string>;
  children: React.ReactNode;
}) {
  return <RouteParamsContext.Provider value={params}>{children}</RouteParamsContext.Provider>;
}

export function useParams<T = Record<string, string>>(): T {
  return React.useContext(RouteParamsContext) as T;
}

// <Navigate to="..." /> — declarative redirect. Uses next/navigation redirect on
// the server and a client-side push after mount otherwise.
export function Navigate({ to }: { to: string; replace?: boolean; state?: unknown }) {
  const router = useRouter();
  React.useEffect(() => {
    router.replace(to);
  }, [to, router]);
  return null;
}

// Passthroughs so any stray imports resolve
export const Outlet = ({ children }: { children?: React.ReactNode }) => <>{children}</>;
export const BrowserRouter = ({ children }: { children?: React.ReactNode }) => <>{children}</>;
export const Routes = ({ children }: { children?: React.ReactNode }) => <>{children}</>;
export const Route = (_props: Record<string, unknown>) => null;
export function useSearchParams(): [URLSearchParams, (v: URLSearchParams) => void] {
  const sp = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  return [sp, () => {}];
}
